import { AssetBodyDtoType, useReportsCreate } from '@/api/WhisApi/generated';
import { useAssetsUploadAsset } from '@/api/WhisApi/generated/assets/assets';
import { showToast } from '@/components/Toast/toast.service';
import { useAppTranslation } from '@/hooks/useAppTranslation';
import { buildUploadFile } from '@/screens/ReportCreation/utils/supportingDocumentsUpload.utils';
import { useQueryClient } from '@tanstack/react-query';
import * as DocumentPicker from 'expo-document-picker';
import { useRouter } from 'expo-router';
import { useCallback, useState } from 'react';
import { buildUploadRraPayload } from './requestRra.utils';
import { UseUploadRraOptions, UseUploadRraResult } from './rra.types';

export const useUploadRra = ({
  report,
  isBoardRoute,
}: UseUploadRraOptions): UseUploadRraResult => {
  const { t } = useAppTranslation();
  const queryClient = useQueryClient();
  const router = useRouter();
  const { mutateAsync: updateReportStatus, isPending: isUpdatingReportStatus } =
    useReportsCreate();
  const { mutateAsync: uploadAsset, isPending: isUploadingAsset } =
    useAssetsUploadAsset();
  const [isPickingRraFile, setIsPickingRraFile] = useState(false);

  const uploadRra = useCallback(async (): Promise<void> => {
    setIsPickingRraFile(true);
    let selectedDocument: DocumentPicker.DocumentPickerAsset | null = null;

    try {
      const pickerResult = await DocumentPicker.getDocumentAsync({
        multiple: false,
      });

      if (!pickerResult.canceled) {
        selectedDocument = pickerResult.assets[0] ?? null;
      }
    } catch (error) {
      console.error('RRA file picker failed', error);
      showToast({
        type: 'error',
        title: t('reportDetail.toasts.uploadRraError'),
      });
      return;
    } finally {
      setIsPickingRraFile(false);
    }

    if (!selectedDocument) {
      return;
    }

    try {
      const file = await buildUploadFile({
        id: selectedDocument.uri,
        uri: selectedDocument.uri,
        name: selectedDocument.name,
        size: selectedDocument.size,
        mimeType: selectedDocument.mimeType,
      });
      const uploadedRraAsset = await uploadAsset({ data: { file } });
      const createdReport = await updateReportStatus({
        data: buildUploadRraPayload(
          report,
          {
            name: uploadedRraAsset.name ?? selectedDocument.name,
            fileName: uploadedRraAsset.fileName,
            type: AssetBodyDtoType.rra,
          },
          t('reportDetail.rra.versionNote'),
        ),
      });

      queryClient.invalidateQueries({
        queryKey: ['api', 'reports'],
      });

      router.replace({
        pathname: isBoardRoute
          ? '/(tabs)/board/reports/[reportId]'
          : '/(tabs)/reports/[reportId]',
        params: { reportId: createdReport.id },
      });
    } catch (error) {
      console.error('RRA upload flow failed', error);
      showToast({
        type: 'error',
        title: t('reportDetail.toasts.uploadRraError'),
      });
    }
  }, [
    isBoardRoute,
    queryClient,
    report,
    router,
    t,
    updateReportStatus,
    uploadAsset,
  ]);

  return {
    isUploadingRra:
      isPickingRraFile || isUploadingAsset || isUpdatingReportStatus,
    uploadRra,
  };
};
